import React, { useEffect, useState } from "react";
import moment from "moment";
import Select from "react-select";
import { AgGridReact } from "ag-grid-react";
import { getNPALoansReport } from "../../../services/npareport";
import { memberlist } from "../../../services/admin";
import { printDataTable } from "../../../utils/printTable";

export default function NPALoansReport() {
    const [loading, setLoading] = useState(true);
    const [npaLoans, setNpaLoans] = useState([]);
    const [summary, setSummary] = useState(null);
    const [members, setMembers] = useState([]);

    // Filters
    const [minUnpaidDues, setMinUnpaidDues] = useState(2);
    const [selectedMember, setSelectedMember] = useState(null);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const columnDefs = [
        {
            headerName: "Loan ID",
            field: "loanid",
            width: 120,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" }
        },
        {
            headerName: "Group",
            field: "group_name",
            width: 200,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" },
            cellRenderer: (params) => (
                <div>
                    <div>{params.data.group_name}</div>
                    <small className="text-muted">{params.data.group_code}</small>
                </div>
            )
        },
        {
            headerName: "Member",
            field: "member_name",
            width: 200,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" },
            cellRenderer: (params) => (
                <div>
                    <div>{params.data.member_name}</div>
                    <small className="text-muted">{params.data.member_code}</small>
                </div>
            )
        },
        {
            headerName: "Phone",
            field: "member_phone",
            width: 140,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" }
        },
        {
            headerName: "Scheme",
            field: "scheme_name",
            width: 180,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" }
        },
        {
            headerName: "Loan Amount",
            field: "loan_amount",
            width: 150,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "right" },
            cellRenderer: function (params) {
                return `₹${parseFloat(params.value).toLocaleString()}`;
            }
        },
        {
            headerName: "Unpaid Dues",
            field: "unpaid_dues_count",
            width: 140,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "center" },
            cellRenderer: (params) => {
                const count = params.value;
                const badgeClass = count >= 4 ? 'bg-danger' : count >= 3 ? 'bg-warning' : 'bg-info';
                return <span className={`badge ${badgeClass}`}>{count}</span>;
            }
        },
        {
            headerName: "Unpaid Amount",
            field: "total_unpaid_amount",
            width: 160,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "right" },
            cellRenderer: function (params) {
                return `₹${parseFloat(params.value).toLocaleString()}`;
            }
        },
        {
            headerName: "Installments",
            field: "unpaid_installments",
            width: 150,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" }
        },
        {
            headerName: "First Due Date",
            field: "first_unpaid_due_date",
            width: 150,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" },
            cellRenderer: function (params) {
                return params.value ? moment(params.value).format("DD/MM/YYYY") : "";
            }
        },
        {
            headerName: "Staff",
            field: "staff_name",
            width: 150,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" }
        }
    ];

    useEffect(() => {
        setFromDate(moment(new Date()).format("YYYY-MM-DD"));
        setToDate(moment(new Date()).format("YYYY-MM-DD"));
        loadMembers();
    }, []);

    // Auto-load report when filters change
    useEffect(() => {
        if (fromDate && toDate) {
            loadNPAReport();
        }
    }, [minUnpaidDues, selectedMember, fromDate, toDate]);

    const loadMembers = () => {
        const temp = new FormData();
        temp.append("branch", localStorage.getItem("branch"));
        memberlist(temp).then((res) => {
            if (res.status === true) {
                const memberOptions = res.data.map(member => ({
                    value: member.id,
                    label: `${member.member_name} ${member.group_name ? `(${member.group_name})` : ''}`
                }));
                setMembers(memberOptions);
            }
        });
    };

    const loadNPAReport = () => {
        setLoading(true);

        const params = {
            min_unpaid_dues: minUnpaidDues,
            member_id: selectedMember ? selectedMember.value : null,
            from_date: fromDate,
            to_date: toDate
        };

        getNPALoansReport(params).then((res) => {
            if (res.data.status === true) {
                setNpaLoans(res.data.data.npa_loans);
                setSummary(res.data.data.summary);
            }
            setLoading(false);
        });
    };

    const handleReset = () => {
        setMinUnpaidDues(2);
        setSelectedMember(null);
        setFromDate(moment(new Date()).format("YYYY-MM-DD"));
        setToDate(moment(new Date()).format("YYYY-MM-DD"));
    };

    const handlePrint = () => {
        printDataTable({
            title: "NPA Loans Report",
            orientation: "landscape",
            meta: [
                `From ${moment(fromDate).format("DD-MM-YYYY")} To ${moment(toDate).format("DD-MM-YYYY")}`,
                `Minimum Unpaid Dues: ${minUnpaidDues}`,
                selectedMember ? `Member: ${selectedMember.label}` : "",
                summary ? `Total NPA Loans: ${summary.total_npa_loans}` : ""
            ],
            columns: [
                { header: "Loan ID", field: "loanid" },
                { header: "Group", value: (row) => [row.group_name, row.group_code].filter(Boolean).join(" - ") },
                { header: "Member", value: (row) => [row.member_name, row.member_code].filter(Boolean).join(" - ") },
                { header: "Phone", field: "member_phone" },
                { header: "Scheme", field: "scheme_name" },
                { header: "Loan Amount", align: "right", value: (row) => parseFloat(row.loan_amount || 0).toLocaleString("en-IN") },
                { header: "Unpaid Dues", align: "center", field: "unpaid_dues_count" },
                { header: "Unpaid Amount", align: "right", value: (row) => parseFloat(row.total_unpaid_amount || 0).toLocaleString("en-IN") },
                { header: "Installments", field: "unpaid_installments" },
                { header: "First Due Date", value: (row) => (row.first_unpaid_due_date ? moment(row.first_unpaid_due_date).format("DD/MM/YYYY") : "") },
                { header: "Staff", field: "staff_name" }
            ],
            rows: npaLoans
        });
    };

    return (
        <div className="app-content main-content">
            <div className="side-app main-container">
                <div className="page-header d-xl-flex d-block">
                    <div className="page-leftheader">
                        <div className="page-title">NPA Loans Report</div>
                        <p className="text-muted">Non-Performing Assets - Loans with unpaid dues</p>
                    </div>
                </div>

                {/* Summary Cards */}
                {summary && (
                    <div className="row">
                        <div className="col-xl-3 col-lg-6 col-md-6 col-xm-12">
                            <div className="card overflow-hidden" style={{ minHeight: '110px' }}>
                                <div className="card-body d-flex flex-column justify-content-between p-3">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <h6 className="text-muted mb-1 small">Total NPA Loans</h6>
                                            <h3 className="mb-0 number-font font-weight-bold">{summary.total_npa_loans}</h3>
                                        </div>
                                        <div className="text-end">
                                            <i className="fa fa-exclamation-triangle fa-lg text-warning"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-3 col-lg-6 col-md-6 col-xm-12">
                            <div className="card overflow-hidden" style={{ minHeight: '110px' }}>
                                <div className="card-body d-flex flex-column justify-content-between p-3">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <h6 className="text-muted mb-1 small">Total NPA Amount</h6>
                                            <h3 className="mb-0 number-font font-weight-bold">₹{summary.total_npa_amount.toLocaleString()}</h3>
                                        </div>
                                        <div className="text-end">
                                            <i className="fa fa-inr fa-lg text-danger"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-3 col-lg-6 col-md-6 col-xm-12">
                            <div className="card overflow-hidden" style={{ minHeight: '110px' }}>
                                <div className="card-body d-flex flex-column justify-content-between p-3">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <h6 className="text-muted mb-1 small">Avg Unpaid Dues</h6>
                                            <h3 className="mb-0 number-font font-weight-bold">{summary.average_unpaid_dues}</h3>
                                        </div>
                                        <div className="text-end">
                                            <i className="fa fa-calculator fa-lg text-info"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-3 col-lg-6 col-md-6 col-xm-12">
                            <div className="card overflow-hidden" style={{ minHeight: '110px' }}>
                                <div className="card-body d-flex flex-column justify-content-between p-3">
                                    <div className="d-flex justify-content-between align-items-start mb-1">
                                        <h6 className="text-muted mb-0 small">Categorization</h6>
                                        <i className="fa fa-list fa-lg text-success"></i>
                                    </div>
                                    <div className="mt-1">
                                        {summary.categorization.map((cat, idx) => (
                                            <div key={idx} className="d-flex justify-content-between align-items-center mb-1">
                                                <span className="small">{cat.unpaid_dues} dues:</span>
                                                <span className="badge bg-primary">{cat.count}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Filters */}
                <div className="row">
                    <div className="col-xl-12">
                        <div className="card">
                            <div className="card-header">
                                <h4 className="card-title">Filters</h4>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-3">
                                        <div className="form-group">
                                            <label>Minimum Unpaid Dues</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={minUnpaidDues}
                                                onChange={(e) => setMinUnpaidDues(e.target.value)}
                                                placeholder="Enter number of unpaid dues"
                                                min="1"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="form-group">
                                            <label>Member</label>
                                            <Select
                                                options={members}
                                                value={selectedMember}
                                                onChange={setSelectedMember}
                                                isClearable
                                                placeholder="Select Member"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-2">
                                        <div className="form-group">
                                            <label>From Date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={fromDate}
                                                onChange={(e) => setFromDate(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-2">
                                        <div className="form-group">
                                            <label>To Date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={toDate}
                                                onChange={(e) => setToDate(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-2">
                                        <div className="form-group">
                                            <label>&nbsp;</label>
                                            <div>
                                                <button
                                                    className="btn btn-secondary"
                                                    onClick={handleReset}
                                                >
                                                    Reset
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* NPA Loans Table */}
                <div className="row">
                    <div className="col-xl-12">
                        <div className="card">
                            <div className="card-header d-flex justify-content-between align-items-center">
                                <h4 className="card-title">NPA Loans List</h4>
                                <button className="btn btn-primary" onClick={handlePrint}>
                                    <i className="fa fa-print"></i> Print
                                </button>
                            </div>
                            <div className="card-body">
                                {loading ? (
                                    <div className="text-center">
                                        <div className="spinner-border" role="status">
                                            <span className="sr-only">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="ag-theme-alpine ag-style mt-2">
                                        <AgGridReact
                                            columnDefs={columnDefs}
                                            rowData={npaLoans}
                                            pagination={true}
                                            paginationPageSize={10}
                                            sortingOrder={["asc", "desc"]}
                                            suppressDragLeaveHidesColumns={true}
                                            domLayout="autoHeight"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
